
import { defineComponent, onMounted, Ref, ref } from "vue";

import { addMonths, compareAsc, startOfMonth } from "date-fns";

import { AppHelpers } from "@/core/modules/helpers/AppHelpers";
import { Company } from "@/features/modules/company/objects/Company";
import { companyModel } from "@/features/modules/company/models/CompanyModel";
import { FormatHelpers } from "@/core/modules/helpers/FormatHelpers";
import { handleReportModel } from "../models/HandleReportModel";
import { HandleReportPaper } from "../paper/HandleReportPaper";
import { LinkedWarehouseLocation } from "@/features/modules/warehouseLocation/objects/LinkedWarehouseLocation";
import { ParcelWithAction } from "../objects/ParcelWithAction";
import { storeTypes } from "@/core/modules/store/types";
import { useLocale } from "@/core/modules/locale/module";
import { useStore } from "@/core/modules/store/module";

export default defineComponent({
  name: "HandleReportView",
  setup() {
    const { d, n, t } = useLocale();

    const results: Ref<ParcelWithAction[]> = ref([]);

    const store = useStore();

    const handleLoad = async () => {
      await AppHelpers.tryOrError(
        async () => {
          await loadAction();
        },
        () => {
          store.commit(storeTypes.mutations.loadingStop);
        }
      );
    };

    const handleSubmit = async () => {
      store.commit(storeTypes.mutations.loadingStart);

      await AppHelpers.tryOrToast(
        async () => {
          await submitAction();
        },
        "calc",
        t,
        () => store.commit(storeTypes.mutations.loadingStop)
      );
    };

    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key == "Enter") {
        const target: HTMLElement = e.target as HTMLElement;
        if (target.localName != "textarea") e.preventDefault();
      }
    };

    /* custom */

    const companyId = ref();
    const monthDate: Ref<Date> = ref(startOfMonth(new Date()));
    monthDate.value.setHours(0, 0, 0, 0);
    const months: Ref<{ label: string; date: Date }[]> = ref([]);
    const handleReportInitialCount = ref(0);
    const handleReportCount = ref(0);
    const handleReportPeak = ref(0);
    const companies: Ref<Company[]> = ref([]);

    const showResults: Ref<boolean> = ref(false);

    const warehouseLocationValue: Ref<string | undefined> = ref(undefined);
    const warehouseLocationVisible: Ref<boolean> = ref(false);

    const dataChanged = () => {
      showResults.value = false;
    };

    const loadAction = async () => {
      companies.value = await companyModel.getActiveCompanies();

      let startDate: Date = new Date("2022-01-01");
      startDate.setHours(0, 0, 0, 0);
      const endDate: Date = startOfMonth(new Date());
      endDate.setHours(0, 0, 0, 0);

      while (compareAsc(startDate, endDate) <= 0) {
        months.value.push({
          label: FormatHelpers.capitalizeFirstLetter(d(startDate, "longMonth")),
          date: startDate,
        });
        startDate = addMonths(startDate, 1);
      }
    };

    const submitAction = async () => {
      showResults.value = false;
      if (companyId.value == undefined) throw new Error("companyIdUndefined");
      if (monthDate.value == undefined) throw new Error("monthDateUndefined");

      results.value = await handleReportModel.calcHandleReport(companyId.value, monthDate.value);

      if (results.value.length > 0) {
        handleReportCount.value = results.value[results.value.length - 1].count ?? 0;
        handleReportInitialCount.value = (results.value[0].count ?? 0) - (results.value[0].action ?? 0);
        handleReportPeak.value = Math.max(...results.value.map((p) => p.count ?? 0));
      }

      showResults.value = true;
    };

    const showWarehouseLocationDetails = (warehouseLocation: LinkedWarehouseLocation) => {
      warehouseLocationValue.value = warehouseLocation.code;
      warehouseLocationVisible.value = true;
    };

    const printReport = () => {
      AppHelpers.tryOrToast(
        async () => {
          store.commit(storeTypes.mutations.loadingStart);

          const company: Company = companies.value.find((c) => c.id == companyId.value) as Company;

          const handleReportPaper: HandleReportPaper = new HandleReportPaper(
            company.name,
            monthDate.value,
            results.value,
            handleReportCount.value,
            handleReportInitialCount.value,
            handleReportPeak.value,
            d,
            n,
            t
          );
          await handleReportPaper.open();
        },
        "actionError",
        t,
        () => store.commit(storeTypes.mutations.loadingStop),
        false
      );
    };

    onMounted(() => handleLoad());

    return {
      companies,
      companyId,
      d,
      dataChanged,
      handleKeydown,
      handleReportCount,
      handleReportInitialCount,
      handleReportPeak,
      handleSubmit,
      monthDate,
      months,
      n,
      printReport,
      results,
      showResults,
      showWarehouseLocationDetails,
      t,
      warehouseLocationValue,
      warehouseLocationVisible,
    };
  },
});
